import {
    CRM_CONTRACT_FIELD_LABELS as L,
    CRM_CONTRACT_FILE_EXTENSIONS,
    CRM_FILE_MAX_SIZE_MB,
    CrmContractCardTemplate,
} from '@/Containers/CrmSection/Components/Contracts'
import { attachUploadedFiles } from '@/Ship/Helpers/attachUploadedFiles'
import { renderDateTimeView, renderDateView, renderUsersView } from '@/Ship/Helpers/fieldViews'

import { CLIENT_CONTRACT_FILE_UPLOAD_URL, CLIENT_CONTRACT_REST_URL } from '../Contracts/IClient'

import type { IResourceConfig } from '@/Core/Contracts/UI/Resource'
import type { IFieldConfig } from '@/Core/Contracts/UI/Form'

/** Лише перегляд: ставить сервер. */
const readOnly = (field: IFieldConfig): IFieldConfig => ({
    ...field,
    isEditable: false,
    inputProps: { ...(field.inputProps ?? {}), disabled: true },
})

/**
 * Поля, яких бек від форми не приймає: службові й назву ставить він сам,
 * `client` задається лише при створенні — змінити власника договору не можна
 * (`SPEC-008` §5.1). Не шлемо їх, щоб тіло запиту не обіцяло зміни, якої не
 * буде.
 */
const READ_ONLY_KEYS = [
    'id',
    'title',
    'externalCode',
    'isRemoved',
    'createdAt',
    'createdBy',
    'updatedAt',
    'updatedBy',
] as const

const prepareContractPayload = ({
    values,
    formContext,
}: {
    values: Record<string, unknown>
    formContext?: { values?: Record<string, unknown> }
}): void => {
    READ_ONLY_KEYS.forEach((key) => delete values[key])

    if (typeof formContext?.values?.['@id'] === 'string') delete values['client']
}

/**
 * Договори клієнта (`SPEC-009` §4.5, §6.4, §8.4–§8.5, `SPEC-008` §4.3).
 *
 * Власного реєстру немає — лише вкладка «Договори» в картці клієнта, тож без
 * `pageUrl`. Створюють договір тільки звідти: клієнт задається при створенні
 * й більше не змінюється (`CREATED_FROM_PARENT`).
 *
 * Права — лише з беку (`SPEC-003` §1.3): «Додати» — колекційне `create` ключа
 * `ClientContract` (бухгалтер, адміністратор), змінити й видалити —
 * `_permissions` договору. Ролей ресурс не питає.
 *
 * Файл — один на договір, двокроково (`SPEC-008` §5.7.1): ядровий `FileInput`
 * одразу вантажить його сиротою на `POST /services/client_contract_files`, а
 * збереження договору прикріплює через `fileUuids` (`attachUploadedFiles`).
 * Новий файл бек ставить замість попереднього. Посилання на файл —
 * підписане, з терміном дії (ADR 042).
 */
export const ClientContractResource: IResourceConfig = {
    title: 'Договори клієнтів',
    fields: {
        number: {
            label: L.number,
            isRequired: true,
            inputProps: { maxLength: 150 },
        },
        date: {
            label: L.date,
            type: 'date',
            isRequired: true,
            view: renderDateView,
        },
        forwarder: {
            label: L.forwarder,
            type: 'select',
            resource: 'Setting.ListLegalEntity',
        },
        file: {
            label: L.file,
            type: 'file',
            // Один файл: новий бек ставить замість попереднього (Н-4)
            isMultiple: false,
            uploadUrl: CLIENT_CONTRACT_FILE_UPLOAD_URL,
            uploadAccept: CRM_CONTRACT_FILE_EXTENSIONS,
            // Стеля — ядровий nginx (`SPEC-008` §5.7.3)
            uploadMaxSizeMb: CRM_FILE_MAX_SIZE_MB,
            showViewIcon: false,
            showEditIcon: false,
        },
        createdAt: readOnly({
            label: L.createdAt,
            type: 'datetime',
            view: renderDateTimeView,
        }),
        createdBy: readOnly({
            label: L.createdBy,
            type: 'select',
            resource: 'Setting.User',
            view: renderUsersView,
        }),
        updatedAt: readOnly({
            label: L.updatedAt,
            type: 'datetime',
            view: renderDateTimeView,
        }),
        updatedBy: readOnly({
            label: L.updatedBy,
            type: 'select',
            resource: 'Setting.User',
            view: renderUsersView,
        }),
    },
    form: {
        eventHandlers: {
            // Крок 2: новий файл — у `fileUuids`, поле `file` — геть із тіла
            onBeforeTransformSendSaveRequest: {
                clientContractFileUuids: attachUploadedFiles('file'),
            },
            onBeforeSendSaveRequest: {
                ClientContractPayload: prepareContractPayload,
            },
        },
    },
    detailConfig: {
        modeConfig: {
            view: { template: CrmContractCardTemplate },
            edit: { template: CrmContractCardTemplate },
            create: { template: CrmContractCardTemplate },
        },
    },
    // Як у легасі (`ClientContractCrud.jsx:16-32`)
    drawer: {
        width: 500,
    },
    restUrl: CLIENT_CONTRACT_REST_URL,
}
